/**
 * Turning an entry's stored documents into something a reader can trust
 * (f1.5, BR-F1.5-13, BR-F1.5-14).
 *
 * Pure functions, no React, so the parts most likely to be silently wrong are
 * testable on their own.
 *
 * Three rules shape everything here:
 *
 * - **The union of keys, not a padded list.** f1.4 records only the fields
 *   that actually changed, so a before/after pair carries the changed fields
 *   and nothing else. A field present on one side only is shown as absent on
 *   the other -- never invented.
 * - **`[REDACTED]` is shown, not hidden.** f1.4 kept the field NAME on purpose:
 *   that a secret was submitted is evidence; which secret is a leak.
 * - **Values render as stored.** A `Decimal` f1.4 wrote as a string is shown
 *   as that string, quoted -- never parsed into a float that could round it.
 */

import { ABSENT_FIELD } from '@/ui/emptyValue';

export const REDACTED = '[REDACTED]';

/** Sentinel the server writes for work that was never an HTTP request. */
export const NON_REQUEST_SENTINEL = '<job>';

export type Json = unknown;
type Fields = Record<string, Json>;

export function isPlainObject(value: Json): value is Fields {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/**
 * Whether a document is f1.4's keyed change set: `{"Type:id": {field: value}}`.
 *
 * Every value must itself be an object. Anything else is rendered as a single
 * flat block rather than being forced into a shape it does not have.
 */
export function isKeyedChangeSet(document: Json): document is Record<string, Fields> {
  return (
    isPlainObject(document) &&
    Object.keys(document).length > 0 &&
    Object.values(document).every(isPlainObject)
  );
}

/** One row of a diff: a field and its value on each side, if present. */
export interface FieldDiff {
  field: string;
  before: { present: boolean; value: Json };
  after: { present: boolean; value: Json };
  changed: boolean;
}

/** One entity's block within a change set. */
export interface EntityDiff {
  /** `Type:id`, or empty for a flat document with no entity keys. */
  entityKey: string;
  fields: FieldDiff[];
}

function sameValue(left: Json, right: Json): boolean {
  return JSON.stringify(left) === JSON.stringify(right);
}

function unionKeys(...documents: Array<Fields | undefined>): string[] {
  const keys: string[] = [];
  for (const document of documents) {
    for (const key of Object.keys(document ?? {})) {
      if (!keys.includes(key)) keys.push(key);
    }
  }
  return keys;
}

function fieldsOf(before: Fields | undefined, after: Fields | undefined): FieldDiff[] {
  return unionKeys(before, after).map((field) => {
    const hasBefore = before !== undefined && Object.hasOwn(before, field);
    const hasAfter = after !== undefined && Object.hasOwn(after, field);
    const beforeValue = hasBefore ? before?.[field] : undefined;
    const afterValue = hasAfter ? after?.[field] : undefined;
    return {
      field,
      before: { present: hasBefore, value: beforeValue },
      after: { present: hasAfter, value: afterValue },
      changed: hasBefore !== hasAfter || !sameValue(beforeValue, afterValue),
    };
  });
}

/**
 * A field-by-field diff of two documents, entity by entity.
 *
 * Handles f1.4's keyed shape (one block per `Type:id`, even when only one
 * entity changed) and, defensively, a flat object (one unkeyed block).
 */
export function diffDocuments(before: Json, after: Json): EntityDiff[] {
  const keyed =
    (before === null || before === undefined || isKeyedChangeSet(before)) &&
    (after === null || after === undefined || isKeyedChangeSet(after)) &&
    (isKeyedChangeSet(before) || isKeyedChangeSet(after));

  if (keyed) {
    const beforeSet = (isKeyedChangeSet(before) ? before : {}) as Record<string, Fields>;
    const afterSet = (isKeyedChangeSet(after) ? after : {}) as Record<string, Fields>;
    return unionKeys(beforeSet, afterSet).map((entityKey) => ({
      entityKey,
      fields: fieldsOf(beforeSet[entityKey], afterSet[entityKey]),
    }));
  }

  const flatBefore = isPlainObject(before) ? before : undefined;
  const flatAfter = isPlainObject(after) ? after : undefined;
  if (!flatBefore && !flatAfter) return [];
  return [{ entityKey: '', fields: fieldsOf(flatBefore, flatAfter) }];
}

/** The fields of a single flat document -- the attempted payload, for one. */
export function fieldsOfDocument(document: Json): Array<{ field: string; value: Json }> {
  if (!isPlainObject(document)) return [];
  return Object.entries(document).map(([field, value]) => ({ field, value }));
}

export function isRedacted(value: Json): boolean {
  return value === REDACTED;
}

/**
 * A stored value as one line of text, exactly as stored.
 *
 * Strings are quoted so `"633.00"` (a Decimal kept as text) is visibly a
 * string and not a number; objects and arrays are compact JSON.
 */
export function renderValue(value: Json): string {
  if (value === undefined) return ABSENT_FIELD;
  if (typeof value === 'string') return JSON.stringify(value);
  if (value === null || typeof value === 'number' || typeof value === 'boolean') {
    return String(value);
  }
  try {
    return JSON.stringify(value);
  } catch {
    return String(value);
  }
}

/** What a truncation marker recorded: the real size and the cap it broke. */
export function truncationSizes(
  before: Json,
  after: Json,
): { originalBytes: number | null; capBytes: number | null } {
  const marker = [after, before].find(
    (document) => isPlainObject(document) && document.__truncated__ === true,
  );
  const read = (key: string): number | null => {
    const value = isPlainObject(marker) ? marker[key] : undefined;
    return typeof value === 'number' ? value : null;
  };
  return { originalBytes: read('original_bytes'), capBytes: read('cap_bytes') };
}

/** Bytes as a short human size: `812 B`, `64.0 KB`, `1.2 MB`. */
export function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/** A context document's top-level entries, for the detail's key/value list. */
export function contextEntries(context: Json): Array<{ key: string; value: string }> {
  if (!isPlainObject(context)) return [];
  return Object.entries(context).map(([key, value]) => ({
    key,
    value: typeof value === 'string' ? value : renderValue(value),
  }));
}

/** The first segment of a UUID -- enough to recognise, short enough to scan. */
export function shortId(id: string): string {
  return id.split('-')[0] ?? id;
}
